import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ah/design';
import { BRAND_TAGLINE } from '@ah/design';
import { Link, Loader2, Search } from 'lucide-react';
import {
  apiPhoneStart,
  apiPhoneVerify,
  apiOnboardingSearch,
  apiOnboardingSetUrl,
  apiOnboardingPreview,
  type LinkedInCandidate,
  type ProfilePreview,
} from '../api';
import { setSessionToken } from '../accounts';

// ─── Countries ────────────────────────────────────────────────────────────

const COUNTRIES = [
  { code: 'US', dialCode: '+1',   name: 'United States',  flag: '🇺🇸' },
  { code: 'CA', dialCode: '+1',   name: 'Canada',         flag: '🇨🇦' },
  { code: 'GB', dialCode: '+44',  name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'AU', dialCode: '+61',  name: 'Australia',      flag: '🇦🇺' },
  { code: 'NZ', dialCode: '+64',  name: 'New Zealand',    flag: '🇳🇿' },
  { code: 'IE', dialCode: '+353', name: 'Ireland',        flag: '🇮🇪' },
  { code: 'IN', dialCode: '+91',  name: 'India',          flag: '🇮🇳' },
  { code: 'SG', dialCode: '+65',  name: 'Singapore',      flag: '🇸🇬' },
  { code: 'MY', dialCode: '+60',  name: 'Malaysia',       flag: '🇲🇾' },
  { code: 'HK', dialCode: '+852', name: 'Hong Kong',      flag: '🇭🇰' },
  { code: 'DE', dialCode: '+49',  name: 'Germany',        flag: '🇩🇪' },
  { code: 'FR', dialCode: '+33',  name: 'France',         flag: '🇫🇷' },
  { code: 'IT', dialCode: '+39',  name: 'Italy',          flag: '🇮🇹' },
  { code: 'ES', dialCode: '+34',  name: 'Spain',          flag: '🇪🇸' },
  { code: 'NL', dialCode: '+31',  name: 'Netherlands',    flag: '🇳🇱' },
  { code: 'CH', dialCode: '+41',  name: 'Switzerland',    flag: '🇨🇭' },
  { code: 'SE', dialCode: '+46',  name: 'Sweden',         flag: '🇸🇪' },
  { code: 'NO', dialCode: '+47',  name: 'Norway',         flag: '🇳🇴' },
  { code: 'DK', dialCode: '+45',  name: 'Denmark',        flag: '🇩🇰' },
  { code: 'BR', dialCode: '+55',  name: 'Brazil',         flag: '🇧🇷' },
  { code: 'MX', dialCode: '+52',  name: 'Mexico',         flag: '🇲🇽' },
  { code: 'AR', dialCode: '+54',  name: 'Argentina',      flag: '🇦🇷' },
  { code: 'JP', dialCode: '+81',  name: 'Japan',          flag: '🇯🇵' },
  { code: 'KR', dialCode: '+82',  name: 'South Korea',    flag: '🇰🇷' },
  { code: 'ZA', dialCode: '+27',  name: 'South Africa',   flag: '🇿🇦' },
  { code: 'AE', dialCode: '+971', name: 'UAE',            flag: '🇦🇪' },
] as const;

type CountryCode = (typeof COUNTRIES)[number]['code'];

function dialCodeFor(code: CountryCode): string {
  return COUNTRIES.find((c) => c.code === code)?.dialCode ?? '+1';
}

/** Combine country dial code + local number into E.164. Strips non-digits and leading zeros. */
function toE164(dialCode: string, local: string): string {
  const digits = local.replace(/\D/g, '').replace(/^0+/, '');
  return `${dialCode}${digits}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function isValidReturnTo(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.hostname === 'localhost' || u.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

type Step = 'phone' | 'otp' | 'choose' | 'set-url' | 'search' | 'confirm';

// ─── Root component ───────────────────────────────────────────────────────

export function Landing() {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') ?? null;

  const [step, setStep] = useState<Step>('phone');
  const [prevStep, setPrevStep] = useState<'set-url' | 'search'>('set-url');
  const [countryCode, setCountryCode] = useState<CountryCode>('SG');
  const [localNumber, setLocalNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [pendingUrl, setPendingUrl] = useState('');
  const [confirmProfile, setConfirmProfile] = useState<ProfilePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-detect country on mount.
  useEffect(() => {
    fetch('https://api.country.is/')
      .then((r) => r.json())
      .then((data: { country?: string }) => {
        const match = COUNTRIES.find((c) => c.code === data.country);
        if (match) setCountryCode(match.code as CountryCode);
      })
      .catch(() => {/* keep default */});
  }, []);

  function completeAuth(token: string) {
    if (returnTo && isValidReturnTo(returnTo)) {
      window.location.href = `${returnTo}#session_token=${encodeURIComponent(token)}`;
    } else {
      setSessionToken(token);
      window.location.reload();
    }
  }

  async function submitPhone(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const phone = toE164(dialCodeFor(countryCode), localNumber);
    setLoading(true);
    try {
      const result = await apiPhoneStart(phone);
      if (!result.ok) setError(result.error ?? 'Failed to send code');
      else setStep('otp');
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  }

  async function submitOtp() {
    if (otp.length !== 6) return;
    setError('');
    setLoading(true);
    const phone = toE164(dialCodeFor(countryCode), localNumber);
    try {
      const result = await apiPhoneVerify(phone, otp);
      if (result.status === 'error') setError(result.error);
      else if (result.status === 'ok') completeAuth(result.token);
      else { setPendingToken(result.pendingToken); setStep('choose'); }
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  }

  /** Shared: go to confirm step. For search results we pass profile data directly;
   *  for paste URL we fetch it from the server first. */
  async function goToConfirm(url: string, knownProfile?: Partial<ProfilePreview>, from: 'set-url' | 'search' = 'set-url') {
    setPendingUrl(url);
    setPrevStep(from);
    setError('');
    if (knownProfile?.name) {
      setConfirmProfile({ name: knownProfile.name ?? null, headline: knownProfile.headline ?? null, pictureUrl: knownProfile.pictureUrl ?? null });
      setStep('confirm');
      return;
    }
    setLoading(true);
    try {
      const preview = await apiOnboardingPreview(pendingToken, url);
      setConfirmProfile(preview);
      setStep('confirm');
    } catch {
      // Still show confirm even without profile data.
      setConfirmProfile({ name: null, headline: null, pictureUrl: null });
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  }

  async function confirmAndCreate() {
    setError('');
    setLoading(true);
    try {
      const result = await apiOnboardingSetUrl(pendingToken, pendingUrl);
      if (result.error) setError(result.error);
      else if (result.token) completeAuth(result.token);
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh grid place-items-center bg-gradient-to-b from-[hsl(34,45%,96%)] to-background px-6">
      <div className="max-w-sm w-full text-center space-y-8 py-16">
        <div className="space-y-2">
          <h1 className="text-5xl font-bold md:text-6xl tracking-tight">Underlings</h1>
          <p className="text-lg text-muted-foreground">{BRAND_TAGLINE}</p>
        </div>

        {step === 'phone' && (
          <form onSubmit={submitPhone} className="space-y-4 text-left">
            <div className="space-y-1.5">
              <Label>Mobile number</Label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={(v) => setCountryCode(v as CountryCode)}>
                  <SelectTrigger className="w-[110px] shrink-0">
                    <SelectValue>
                      {COUNTRIES.find((c) => c.code === countryCode)?.flag} {dialCodeFor(countryCode)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.dialCode} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  placeholder="9123 4567"
                  value={localNumber}
                  onChange={(e) => setLocalNumber(e.target.value)}
                  autoComplete="tel-national"
                  required
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">We'll send a code via WhatsApp.</p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Sending…' : 'Send code'}
            </Button>
          </form>
        )}

        {step === 'otp' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code sent to{' '}
              <span className="font-medium text-foreground">
                {dialCodeFor(countryCode)} {localNumber}
              </span>
            </p>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} onComplete={submitOtp}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button className="w-full" onClick={submitOtp} disabled={loading || otp.length !== 6}>
              {loading ? 'Verifying…' : 'Verify'}
            </Button>
            <button type="button" className="text-xs text-muted-foreground underline underline-offset-2"
              onClick={() => { setStep('phone'); setOtp(''); setError(''); }}>
              Change number
            </button>
          </div>
        )}

        {step === 'choose' && <ChooseStep onSelect={setStep} />}

        {step === 'set-url' && (
          <SetUrlStep
            pendingToken={pendingToken}
            loading={loading}
            onConfirm={(url) => goToConfirm(url, undefined, 'set-url')}
            onBack={() => setStep('choose')}
          />
        )}

        {step === 'search' && (
          <SearchStep
            pendingToken={pendingToken}
            onConfirm={(url, candidate) => goToConfirm(url, { name: candidate.name, headline: candidate.headline, pictureUrl: candidate.thumbnailUrl }, 'search')}
            onBack={() => setStep('choose')}
          />
        )}

        {step === 'confirm' && (
          <ConfirmStep
            linkedinUrl={pendingUrl}
            profile={confirmProfile}
            loading={loading}
            error={error}
            onConfirm={confirmAndCreate}
            onBack={() => setStep(prevStep)}
          />
        )}

        <p className="text-xs text-muted-foreground">
          By logging in, you agree to our{' '}
          <a href="#" className="underline hover:text-foreground">Terms</a> and{' '}
          <a href="#" className="underline hover:text-foreground">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

// ─── Sub-steps ────────────────────────────────────────────────────────────

function ChooseStep({ onSelect }: { onSelect: (s: Step) => void }) {
  return (
    <div className="space-y-3 text-left">
      <p className="text-sm text-muted-foreground text-center">Connect your LinkedIn profile to continue.</p>
      <button type="button" onClick={() => onSelect('set-url')}
        className="w-full flex items-center gap-4 rounded-lg border bg-card p-4 hover:bg-accent transition-colors">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Link size={18} />
        </div>
        <div className="text-left">
          <p className="font-medium text-sm">Paste your LinkedIn URL</p>
          <p className="text-xs text-muted-foreground mt-0.5">Copy it from your LinkedIn profile</p>
        </div>
      </button>
      <button type="button" onClick={() => onSelect('search')}
        className="w-full flex items-center gap-4 rounded-lg border bg-card p-4 hover:bg-accent transition-colors">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Search size={18} />
        </div>
        <div className="text-left">
          <p className="font-medium text-sm">Search for yourself</p>
          <p className="text-xs text-muted-foreground mt-0.5">Search by name or company</p>
        </div>
      </button>
    </div>
  );
}

function SetUrlStep({
  onConfirm,
  loading,
  onBack,
}: {
  pendingToken: string;
  loading: boolean;
  onConfirm: (url: string) => void;
  onBack: () => void;
}) {
  const [url, setUrl] = useState('');
  const [localError, setLocalError] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError('');
    if (!url.includes('linkedin.com/in/')) {
      setLocalError('Please enter a valid LinkedIn profile URL');
      return;
    }
    onConfirm(url);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
      <div className="space-y-1.5">
        <Label htmlFor="linkedin-url">LinkedIn profile URL</Label>
        <Input id="linkedin-url" type="url" placeholder="https://linkedin.com/in/your-name"
          value={url} onChange={(e) => setUrl(e.target.value)} required />
      </div>
      {localError && <p className="text-sm text-destructive">{localError}</p>}
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? <><Loader2 size={14} className="mr-2 animate-spin" />Looking up…</> : 'Continue'}
      </Button>
      <button type="button" onClick={onBack} className="w-full text-xs text-muted-foreground underline underline-offset-2">Back</button>
    </form>
  );
}

function SearchStep({
  pendingToken,
  onConfirm,
  onBack,
}: {
  pendingToken: string;
  onConfirm: (url: string, candidate: LinkedInCandidate) => void;
  onBack: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LinkedInCandidate[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    setError('');
    setSearching(true);
    try {
      const data = await apiOnboardingSearch(pendingToken, query);
      setResults(data);
      if (data.length === 0) setError('No results — try a different name or company');
    } catch {
      setError('Search failed — please try again');
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="space-y-4 text-left">
      <form onSubmit={handleSearch} className="flex gap-2">
        <Input placeholder="Your name, company…" value={query}
          onChange={(e) => setQuery(e.target.value)} className="flex-1" />
        <Button type="submit" disabled={searching || !query.trim()} size="icon" variant="outline">
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        </Button>
      </form>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {results !== null && results.length > 0 && (
        <ul className="space-y-2">
          {results.map((r) => (
            <li key={r.linkedinUrl}>
              <button type="button" onClick={() => onConfirm(r.linkedinUrl, r)}
                className="w-full flex items-center gap-3 rounded-lg border bg-card p-3 text-left hover:bg-accent transition-colors">
                <Avatar className="size-10 shrink-0">
                  {r.thumbnailUrl && <AvatarImage src={r.thumbnailUrl} alt={r.name} />}
                  <AvatarFallback className="text-xs">{r.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{r.name}</p>
                  {r.headline && <p className="text-xs text-muted-foreground truncate">{r.headline}</p>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={onBack} className="w-full text-xs text-muted-foreground underline underline-offset-2">Back</button>
    </div>
  );
}

function ConfirmStep({
  linkedinUrl,
  profile,
  loading,
  error,
  onConfirm,
  onBack,
}: {
  linkedinUrl: string;
  profile: ProfilePreview | null;
  loading: boolean;
  error: string;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const vanity = linkedinUrl.match(/linkedin\.com\/in\/([^/?#]+)/i)?.[1] ?? '';
  const displayName = profile?.name ?? vanity;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-card p-5 flex flex-col items-center gap-3 text-center">
        <Avatar className="size-20">
          {profile?.pictureUrl && <AvatarImage src={profile.pictureUrl} alt={displayName} />}
          <AvatarFallback className="text-xl">
            {displayName.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div>
          {displayName && <p className="font-semibold text-base">{displayName}</p>}
          {profile?.headline && <p className="text-sm text-muted-foreground mt-0.5">{profile.headline}</p>}
          <p className="text-xs text-muted-foreground mt-1 break-all">{linkedinUrl}</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        You may be required to verify your identity later.
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex flex-col gap-2">
        <Button className="w-full" onClick={onConfirm} disabled={loading}>
          {loading ? <><Loader2 size={14} className="mr-2 animate-spin" />Creating account…</> : 'This is me — continue'}
        </Button>
        <button type="button" onClick={onBack}
          className="text-xs text-muted-foreground underline underline-offset-2">
          Not me — go back
        </button>
      </div>
    </div>
  );
}
